import React from 'react';
import { useSetAtom } from 'jotai';
import { FocusHandler } from '@noriginmedia/norigin-spatial-navigation';
import ListItem from '@smart-tv/components/list';
import { npvrNotificationStateAtom } from '@smart-tv/atoms';
import useNpvrNotifications from '@smart-tv/hooks/use-npvr-notifications';
import getNpvrNotificationState from '@smart-tv/utils/kmm/getNpvrNotificationState';
import { npvrFailedDialogResetAtom } from '@smart-tv/components/npvr/npvr-failed-recordings-dialog';

interface Props {
  listItemClassName?: string;
  onFocus?: FocusHandler;
}

// Bypasses the real API so the notification dots (header, settings menu, npvr tabs) can be
// tested without depending on live backend data. Enabling test mode also stops refresh()/
// markTabSeen() from making real get/set calls that would otherwise overwrite the atom.
export const DeveloperNpvrTest: React.FC<Props> = ({ listItemClassName, onFocus }) => {
  const setNpvrNotificationAtomState = useSetAtom(npvrNotificationStateAtom);
  const { testMode, setTestMode } = useNpvrNotifications();
  const resetFailedDialog = useSetAtom(npvrFailedDialogResetAtom);
  const [resetStatus, setResetStatus] = React.useState('');

  const handleResetFailedDialog = React.useCallback(() => {
    setTestMode(true);
    setNpvrNotificationAtomState((current) => ({
      dotExpiringSoonEventsShown: false,
      dialogExpiringSoonEventsShown: false,
      dotScheduledEventsShown: false,
      dialogScheduledEventsShown: false,
      dotFailedEventsShown: false,
      dotGlobalShown: false,
      ...current,
      dialogFailedEventsShown: true,
      dialogGlobalShown: true
    }));
    resetFailedDialog((version) => version + 1);
    setResetStatus('Test dialog ready');
  }, [resetFailedDialog, setTestMode, setNpvrNotificationAtomState]);

  const handleToggleNpvrTestMode = React.useCallback(() => {
    const next = !testMode;
    setTestMode(next);

    if (!next) {
      // Restore the live backend state instead of leaving the synthetic test data in place.
      (async () => {
        try {
          const liveState = await getNpvrNotificationState();
          setNpvrNotificationAtomState(liveState);
        } catch (error) {
          console.error(error);
        }
      })();
      return;
    }

    setNpvrNotificationAtomState({
      dotExpiringSoonEventsShown: next,
      dialogExpiringSoonEventsShown: false,
      dotScheduledEventsShown: next,
      dialogScheduledEventsShown: false,
      dotFailedEventsShown: next,
      dialogFailedEventsShown: false,
      dotGlobalShown: next,
      dialogGlobalShown: false
    });
  }, [testMode, setTestMode, setNpvrNotificationAtomState]);

  return (
    <>
      <ListItem
        listItemProps={{ className: listItemClassName }}
        onEnterPress={handleToggleNpvrTestMode}
        onFocus={onFocus}
        data={['Enter NPVR test mode', testMode ? 'Active' : 'Deactivated']}
      />
      <ListItem
        listItemProps={{ className: listItemClassName }}
        onEnterPress={handleResetFailedDialog}
        onFocus={onFocus}
        data={
          resetStatus
            ? ['Replay failed recordings dialog (test mode)', resetStatus]
            : ['Replay failed recordings dialog (test mode)']
        }
      />
    </>
  );
};

export default DeveloperNpvrTest;
